import React, { useMemo, useRef } from 'react';
import { Box, Theme, useTheme } from '@mui/material';
import { SxProps } from '@mui/material';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import {
    DContextMenu,
    DContextMenuItem,
    handleMouseEvent,
    TCustomTableBodyCellTextAlign,
    useDContextMenu,
} from '../../../../../dum';
import {
    isInteractiveTarget,
    useTouchDoubleActivation,
} from '../../../../../../helpers';

export interface VirtualColumn<T> {
    title: string;
    content: (row: T, index: number) => React.ReactNode;
    textAlign?: TCustomTableBodyCellTextAlign;
    cellGrow?: number;
    cellSx?: (row: T) => SxProps<Theme>;
}

export interface VirtualContextItem<T> {
    title: string;
    callback: (row: T) => void;
    disable?: (row: T) => boolean;
}

interface VirtualTransactionTableProps<T> {
    columns: VirtualColumn<T>[];
    data: T[];
    /** px height of each body row */
    rowHeight?: number;
    /** px height of the scroll viewport */
    height?: number;
    onRowDoubleClick?: (row: T) => void;
    rowSx?: (row: T) => SxProps<Theme>;
    contextItems?: VirtualContextItem<T>[];
    getRowKey?: (row: T, index: number) => React.Key;
    /**
     * Optional band number per row. When provided, the background shading
     * follows the parity of this number instead of the row index, and a
     * heavier top border is drawn wherever it changes — so consecutive rows
     * that share a band read as one visual group.
     */
    getRowBand?: (row: T, index: number) => number;
}

// Shared flex layout for a single cell, driven by the column's cellGrow so the
// header and body columns line up exactly (same idea as CTableWrapper widths).
function cellFlexSx(
    col: VirtualColumn<unknown>,
    align: TCustomTableBodyCellTextAlign,
): SxProps<Theme> {
    return {
        flexGrow: col.cellGrow || 1,
        flexBasis: 0,
        minWidth: 0,
        px: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent:
            align === 'right'
                ? 'flex-end'
                : align === 'center'
                ? 'center'
                : 'flex-start',
        textAlign: align,
        overflow: 'hidden',
    };
}

export function VirtualTransactionTable<T>({
    columns,
    data,
    rowHeight = 44,
    height = 480,
    onRowDoubleClick,
    rowSx,
    contextItems,
    getRowKey,
    getRowBand,
}: VirtualTransactionTableProps<T>) {
    const theme = useTheme();
    const rowAlternateColor =
        theme.palette.mode === 'dark' ? '#303030' : '#E0E0E0';

    const [contextMenu, setContextMenu] = useDContextMenu();
    const clickedItem = useRef<T | null>(null);

    const contextMenuItems: DContextMenuItem[] = useMemo(() => {
        if (!contextItems) return [];
        return contextItems.map((item) => ({
            title: item.title,
            callback: () => {
                if (clickedItem.current) item.callback(clickedItem.current);
            },
            disable: () =>
                item.disable && clickedItem.current
                    ? item.disable(clickedItem.current)
                    : false,
        }));
    }, [contextItems]);

    // The viewport never grows beyond the data it holds — a short list shows a
    // short box instead of empty padding.
    const viewportHeight = Math.min(height, Math.max(rowHeight, data.length * rowHeight));

    const Row = ({ index, style }: ListChildComponentProps) => {
        const row = data[index];
        // Band drives the striping: with getRowBand, rows sharing a band share
        // a shade and the next band flips it; without it we fall back to plain
        // every-other-row striping. A band change marks a new visual group, so
        // we draw a heavier line above it to separate the groups.
        const band = getRowBand ? getRowBand(row, index) : index;
        const isGroupStart =
            !!getRowBand &&
            index > 0 &&
            band !== getRowBand(data[index - 1], index - 1);
        const handleTouchEnd = useTouchDoubleActivation(
            onRowDoubleClick ? () => onRowDoubleClick(row) : undefined,
            getRowKey ? getRowKey(row, index) : index,
        );
        return (
            <Box
                style={style}
                onDoubleClick={
                    onRowDoubleClick
                        ? (e: React.MouseEvent) => {
                              if (
                                  e.currentTarget instanceof HTMLElement &&
                                  isInteractiveTarget(e, e.currentTarget)
                              )
                                  return;
                              onRowDoubleClick(row);
                          }
                        : undefined
                }
                onTouchEnd={onRowDoubleClick ? handleTouchEnd : undefined}
                onKeyDown={
                    onRowDoubleClick
                        ? (e: React.KeyboardEvent) => {
                              if (e.key !== 'Enter') return;
                              if (
                                  e.currentTarget instanceof HTMLElement &&
                                  isInteractiveTarget(e, e.currentTarget)
                              )
                                  return;
                              e.preventDefault();
                              onRowDoubleClick(row);
                          }
                        : undefined
                }
                tabIndex={onRowDoubleClick ? 0 : undefined}
                title={
                    onRowDoubleClick
                        ? 'Doble clic o doble toque para abrir'
                        : undefined
                }
                onContextMenu={
                    contextItems
                        ? (e: React.MouseEvent) => {
                              handleMouseEvent(e, setContextMenu, contextMenu);
                              clickedItem.current = row;
                          }
                        : undefined
                }
                sx={{
                    display: 'flex',
                    alignItems: 'stretch',
                    boxSizing: 'border-box',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    borderTop: isGroupStart
                        ? `2px solid ${theme.palette.text.secondary}`
                        : undefined,
                    bgcolor:
                        band % 2 === 1 ? rowAlternateColor : 'background.paper',
                    cursor: onRowDoubleClick ? 'pointer' : 'default',
                    touchAction: onRowDoubleClick ? 'manipulation' : undefined,
                    '&:hover': onRowDoubleClick
                        ? { filter: 'brightness(0.97)' }
                        : undefined,
                    ...(onRowDoubleClick
                        ? {
                              '&:focus-visible': {
                                  outline: '2px solid',
                                  outlineColor: 'primary.main',
                                  outlineOffset: -2,
                              },
                          }
                        : {}),
                    ...(rowSx ? (rowSx(row) as object) : {}),
                }}
            >
                {columns.map((col, ci) => {
                    const align = col.textAlign || 'left';
                    return (
                        <Box
                            key={ci}
                            sx={{
                                ...cellFlexSx(col as VirtualColumn<unknown>, align),
                                ...(col.cellSx ? (col.cellSx(row) as object) : {}),
                            }}
                        >
                            {col.content(row, index)}
                        </Box>
                    );
                })}
            </Box>
        );
    };

    return (
        <Box
            sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '5px',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'stretch',
                    bgcolor: 'background.default',
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    fontWeight: 700,
                }}
            >
                {columns.map((col, ci) => {
                    const align = col.textAlign || 'left';
                    return (
                        <Box
                            key={ci}
                            sx={{
                                ...cellFlexSx(col as VirtualColumn<unknown>, align),
                                py: 1,
                                fontWeight: 700,
                            }}
                        >
                            {col.title}
                        </Box>
                    );
                })}
            </Box>

            {/* Body */}
            {data.length > 0 ? (
                <FixedSizeList
                    height={viewportHeight}
                    itemCount={data.length}
                    itemSize={rowHeight}
                    width={'100%'}
                    itemKey={
                        getRowKey
                            ? (index) => getRowKey(data[index], index)
                            : undefined
                    }
                >
                    {Row}
                </FixedSizeList>
            ) : (
                <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                    No hay resultados
                </Box>
            )}

            {contextItems && contextItems.length > 0 ? (
                <DContextMenu
                    contextMenu={contextMenu}
                    setContextMenu={setContextMenu}
                    contextMenuItems={contextMenuItems}
                    onClose={() => {
                        // keep clickedItem until next open
                    }}
                />
            ) : null}
        </Box>
    );
}
